<script setup lang="ts">
import { onMounted, ref, watch, computed, onBeforeUnmount } from 'vue'
import { useWindowScroll } from '@vueuse/core'
import { getMercariUrl, getKeywordTabUrl } from '@/lib/mercari'
import SearchInput from '@/components/ui/input/SearchInput.vue'
import type {
	IMercariProductListFetchCompleteEvent,
	IMercariProductListItemClickEvent,
} from '@/components/MercariProductList.vue'
import BackButton from '@/components/BackButton.vue'
import MercariFilterPanel from '@/pages/mercari-search/components/_MercariFilterPanel.vue'
import { useMercariFilter } from '@/lib/useMercariFilter'
import { getSortingMethodLabel } from '@/lib/mercariFilterConfig'
import MercariSortModal from './_MercariFilterSortModal.vue'
import MercariFilterBottomSheet from '@/pages/mercari-search/components/_MercariFilterBottomSheet.vue'
import MercariFilterDrawer from '@/pages/mercari-search/components/_MercariFilterDrawer.vue'
import MercariSortSheet from '@/pages/mercari-search/components/_MercariFilterSortSheet.vue'
import MercariProductList from '@/components/MercariProductList.vue'
import type { IMercariProductListItemContent } from '@/components/MercariProductListItem.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import MercariSearchBar from '@/components/ui/mercari/MercariSearchBar.vue'
import { fetchMercariCommonInfo } from '@/assets/ts/apiManagement'
import { useBreakpoints } from '@/lib/utils'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import {
	MercariSearchMode,
	type MercariSearchParams,
	isShopModeParams,
	isKeywordModeParams,
	isCategoryModeParams,
	buildSearchParamsFromUrl,
	getDisplayValue,
	formatTrackingKeyword,
} from '@/types/Mercari'

const props = defineProps<{
	pageContent: {
		heroTitle: string
		sellingPointSection: {
			sellingPoint_title: string
			sellingPoint_description: string
		}[]
		trendingKeywordSectionTitle: string
		productItem: IMercariProductListItemContent
		productListTitle: string
		productListLoadMoreButtonText: string
		searchPlaceholder: string
		backButtonText: string
		searchResultsTitle: string
		searchResultsTitleNoKeyword: string
		searchResultsTitleCategory: string
		edgeCase: {
			empty: {
				description: string
			}
			serverError: {
				title: string
				description: string
				buttonText: string
			}
		}
		filterContent: any
		translate?: {
			description: string
			buttonText: string
			translateTarget?: string
			translateOriginal?: string
		}
	}
}>()

// handle search query from url (local: getQueryParams , prod: getPath)
const getSearchQueryFromUrl = (): Record<string, string> => {
	const url = new URL(window.location.href)
	const params = new URLSearchParams(url.search)

	const rawQuery = params.get('q') || ''
	const cleanedQuery = rawQuery.trim()

	return {
		q: cleanedQuery,
		shop_id: params.get('shop_id') || '',
		shop_name: params.get('shop_name') || '',
		category_id: params.get('category_id') || '',
	}
}

const urlParamsObj = getSearchQueryFromUrl()
const searchParams = ref<MercariSearchParams>(
	buildSearchParamsFromUrl(urlParamsObj),
)

const tempInputValue = ref(getDisplayValue(searchParams.value))

const apiKeyword = computed(() =>
	isKeywordModeParams(searchParams.value) ? searchParams.value.keyword : '',
)
const apiShopId = computed(() =>
	isShopModeParams(searchParams.value) ? searchParams.value.shop_id : '',
)
const apiCategoryId = computed(() =>
	isCategoryModeParams(searchParams.value)
		? searchParams.value.category_id
		: '',
)

const categoryName = computed(() => {
	if (!apiCategoryId.value || !mercariFilter.categoryLevels?.value) return ''

	const foundOption = mercariFilter.categoryLevels.value
		.flatMap(level => level.options)
		.find(opt => opt.id === apiCategoryId.value && opt.id !== 'all')

	return foundOption?.label || ''
})

const isSheetOpen = ref(false)
const keywordTabs = ref<{ shopId: string; label: string; value: string }[]>([])
const { isSmall, isTabletPortraitScreen, isTabletLandscapeScreen } =
	useBreakpoints()

const goBackURL = getMercariUrl('index')

const productListRef = ref<InstanceType<typeof MercariProductList>>()

const mercariFilter = useMercariFilter({
	productListRef,
	locationId: GaGeneralPageName.MercariIndex,
	filterContent: props.pageContent.filterContent,
	keyword: apiKeyword.value,
})

const { filterState, handleSortChange } = mercariFilter

const hasSearchResults = computed(() =>
	Boolean(productListRef.value?.state.productList.length),
)

// If the search-page is initialized in shopMode, it will switch to keywordMode when newKeyword search is triggered.
const handleSearch = async (value?: string) => {
	const searchValue = tempInputValue.value.trim()

	if (!searchValue) return

	const isShopMode = isShopModeParams(searchParams.value)

	mercariFilter.clearAllFilters(true)

	isTranslated.value = true

	apiSearchKeyword.value = ''

	searchParams.value = {
		mode: MercariSearchMode.KEYWORD,
		keyword: searchValue,
	}

	updateBrowserUrlWithSearchParams()
	sendSearchTrackingEvent(isShopMode)
	isSheetOpen.value = false
	tempInputValue.value = getDisplayValue(searchParams.value)
}

const updateBrowserUrlWithSearchParams = () => {
	let payload: any

	if (isShopModeParams(searchParams.value)) {
		payload = {
			shop_id: searchParams.value.shop_id,
			shop_name: searchParams.value.shop_name || '',
		}
	} else if (isCategoryModeParams(searchParams.value)) {
		payload = {
			category_id: searchParams.value.category_id,
		}
	} else {
		payload = {
			searchQuery: searchParams.value.keyword,
		}
	}

	const newUrl = getMercariUrl('search', payload)
	window.history.pushState(
		{ search: JSON.stringify(searchParams.value) },
		'',
		newUrl,
	)
}

const sendSearchTrackingEvent = (isShopMode: boolean) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search Submitted',
		keyword: tempInputValue.value,
		event_extra: isShopMode ? 'Shop' : 'Keyword',
	})
}

const handlePopState = () => {
	const urlParams = getSearchQueryFromUrl()
	searchParams.value = buildSearchParamsFromUrl(urlParams)
	tempInputValue.value = getDisplayValue(searchParams.value)
}

const handleFetchComplete = (data: IMercariProductListFetchCompleteEvent) => {
	if (!data.success) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.MercariIndex,
			button_name: 'Try Again',
		})
	} else if (data.searchKeyword) {
		apiSearchKeyword.value = data.searchKeyword
	}
}

const handleItemClick = (data: IMercariProductListItemClickEvent) => {
	const formattedKeyword = formatTrackingKeyword(searchParams.value)

	const filterValues = mercariFilter.getFilterTrackingValues()

	const categoryValues = mercariFilter.categoryLevels?.value
		? mercariFilter.createCategoryValuesFromLevels(
				mercariFilter.categoryLevels.value,
		  )
		: {}

	const sortingMethodLabel = getSortingMethodLabel(
		mercariFilter.filterState.sort,
	)

	createGAEvent('select_item', {
		page_name: GaGeneralPageName.MercariIndex,
		keyword_translation: isTranslated.value ? 'On' : 'Off',
		sorting_method: sortingMethodLabel,
		item_category: categoryValues.item_category || '',
		item_category2: categoryValues.item_category2 || '',
		item_category3: categoryValues.item_category3 || '',
		item_category4: categoryValues.item_category4 || '',
		filter_condition: filterValues.filter_condition || '',
		filter_availability: filterValues.filter_availability || '',
		filter_price: filterValues.filter_price || '',
		filter_lister: filterValues.filter_lister || '',
		items: [
			{
				location_id: GaGeneralPageName.MercariIndex,
				item_list_name: 'Product Listing',
				item_id: `product_${data.item.id}`,
				item_name: data.item.name,
				item_category: 'Mercari',
				index: data.index + 1,
				keyword: formattedKeyword,
			},
		],
	})

	window.open(data.targetUrl, '_blank')
}

const handleSearchInputClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search',
	})
}

// Use scroll position
const { y: scrollY } = useWindowScroll()

// Show back button when scrolled past product list
const isShowBackToTopButton = computed(() => {
	return scrollY.value > window.innerHeight * 0.33
})

const scrollToTop = () => {
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	})
}

const closeSheet = () => {
	isSheetOpen.value = false
}

const sectionGapClass = computed(() => ({
	'gap-[32px]': hasSearchResults.value,
	'gap-[60px]': !hasSearchResults.value,
}))

const searchResultsTitleClass = computed(() => ({
	'typography-s1-str': apiCategoryId.value,
	'typography-b2-reg': !apiCategoryId.value,
}))

const isTranslated = ref(true)
const apiSearchKeyword = ref('')

const searchLang = computed(() => (isTranslated.value ? 'native' : undefined))

const shouldShowSearchResultsTitle = computed(() => {
	if (apiCategoryId.value && categoryName.value) {
		return true
	}

	if (!apiKeyword.value) {
		return Boolean(props.pageContent?.searchResultsTitleNoKeyword)
	}

	return apiKeyword.value && apiSearchKeyword.value
})

const shouldShowTranslateButton = computed(() => {
	const isOriginalJapanese =
		isOriginalKeyword.value && searchLang.value === 'native'

	return shouldShowSearchResultsTitle.value && !isOriginalJapanese
})

const isOriginalKeyword = computed(() => {
	return apiKeyword.value === apiSearchKeyword.value
})

const searchResultsTitle = computed(() => {
	if (apiCategoryId.value && categoryName.value) {
		const template = props.pageContent.searchResultsTitleCategory || ''
		return template.replace('{keyword}', categoryName.value)
	}

	if (!apiKeyword.value) {
		return props.pageContent?.searchResultsTitleNoKeyword
	}

	if (!apiSearchKeyword.value) {
		return ''
	}

	const isOriginalJapanese =
		isOriginalKeyword.value && searchLang.value === 'native'

	// if original keyword is japanese, show original result
	if (isOriginalJapanese) {
		const template = props.pageContent.searchResultsTitle || ''
		return template.replace('{keyword}', apiSearchKeyword.value)
	}

	// if translate is enabled, show translated result
	if (props.pageContent.translate) {
		const template = props.pageContent.translate.description || ''
		return template.replace('{keyword}', apiSearchKeyword.value)
	}

	// fallback
	const template = props.pageContent.searchResultsTitle || ''
	return template.replace('{keyword}', apiSearchKeyword.value)
})

const handleKeywordTabClick = (value: string) => {
	const selectedTab = keywordTabs.value.find(tab => tab.value === value)

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search Submitted',
		keyword: value,
		event_extra: selectedTab?.shopId ? 'Shop' : 'Trending',
	})

	window.location.href = getKeywordTabUrl(value, keywordTabs.value)
}

const fetchKeywordTabs = async () => {
	const MAX_KEYWORD_TABS = 6

	try {
		const commonInfo = await fetchMercariCommonInfo()

		const promotionShops = commonInfo.promotionShops.map(shop => ({
			shopId: shop.shop_id,
			label: shop.display_name,
			value: shop.display_name,
		}))
		const keywords = commonInfo.keywords.map(keyword => ({
			shopId: '',
			label: keyword,
			value: keyword,
		}))
		keywordTabs.value = [...promotionShops, ...keywords].slice(
			0,
			MAX_KEYWORD_TABS,
		)
	} catch (error) {
		console.error('Failed to fetch mercari keyword tabs:', error)
	}
}

const handleMercariLogoClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Merchant Logo',
	})
}
const handleTranslateButtonClick = () => {
	isTranslated.value = !isTranslated.value
	reloadProductListWithTranslation()

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search Submitted',
		keyword: apiKeyword.value,
		keyword_translation: isTranslated.value ? 'On' : 'Off',
	})
}

const reloadProductListWithTranslation = () => {
	if (!productListRef.value) return

	apiSearchKeyword.value = ''

	productListRef.value.state.page = 0
	productListRef.value.state.productList = []

	const extraParams = {
		searchLang: searchLang.value,
	}

	productListRef.value.fetchProducts(extraParams)
}

// tablet: sort and filter options
const isSortModalOpen = ref(false)
// mobile: sort and filter options
const isSortSheetOpen = ref(false)
// mobile: filter options
const isFilterBottomSheetOpen = ref(false)
// tablet: filter options
const isFilterDrawerOpen = ref(false)

const handleSortModalOpen = () => {
	isSortModalOpen.value = true
}

const handleSortSheetOpen = () => {
	isSortSheetOpen.value = true
}

const handleFilterButtonClick = async () => {
	if (
		!mercariFilter.categoryLevels ||
		!mercariFilter.categoryLevels.value ||
		mercariFilter.categoryLevels.value.length === 0 ||
		!mercariFilter.categoryLevels.value[0] ||
		mercariFilter.categoryLevels.value[0].options.length === 0
	) {
		await mercariFilter.initialize()
	}

	if (isTabletPortraitScreen.value || isTabletLandscapeScreen.value) {
		isFilterDrawerOpen.value = true
	} else {
		isFilterBottomSheetOpen.value = true
	}
}

const handleSortModalApply = (value: string) => {
	const mockEvent = {
		target: {
			value,
			options: props.pageContent.filterContent.sort.options.map((opt: any) => ({
				value: opt.id,
				textContent: opt.label,
			})),
			selectedIndex: props.pageContent.filterContent.sort.options.findIndex(
				(opt: any) => opt.id === value,
			),
		},
	} as unknown as Event

	handleSortChange(mockEvent)
}

const getActiveFilterCount = () => {
	const filters = [
		filterState.category && filterState.category !== 'all',
		filterState.itemCondition?.length > 0,
		filterState.status?.length > 0,
		filterState.price?.min && filterState.price.min !== '',
		filterState.price?.max && filterState.price.max !== '',
		filterState.marketplace?.length > 0,
	]

	return filters.filter(Boolean).length
}

const handleCategoryChanged = (newCategoryId: string) => {
	// If there's already a keyword, keep KEYWORD mode with category as filter
	if (apiKeyword.value) {
		// Keep existing keyword mode, don't change searchParams
		return
	}

	// Only switch modes when there's no keyword
	if (newCategoryId === 'all') {
		searchParams.value = {
			mode: MercariSearchMode.KEYWORD,
			keyword: '',
		}
	} else {
		searchParams.value = {
			mode: MercariSearchMode.CATEGORY,
			category_id: newCategoryId,
		}
	}
}

onMounted(async () => {
	fetchKeywordTabs()
	window.addEventListener('popstate', handlePopState)

	await mercariFilter.initialize()

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.MercariIndex,
	})

	tempInputValue.value = getDisplayValue(searchParams.value)
})

onBeforeUnmount(() => {
	window.removeEventListener('popstate', handlePopState)
})

watch(isSmall, newValue => {
	if (!newValue) {
		isSheetOpen.value = false
	}
})

watch(apiKeyword, newKeyword => {
	mercariFilter.updateKeyword?.(newKeyword)
})
</script>

<template>
	<section class="bs-container">
		<div
			class="col-span-12 pb-[37px] pt-20px mdp:pb-[60px] mdp:pt-0 lg:mt-32px"
		>
			<div class="flex flex-col" :class="sectionGapClass">
				<MercariSearchBar
					v-model="tempInputValue"
					v-model:isSheetOpen="isSheetOpen"
					:placeholder="props.pageContent.searchPlaceholder"
					:keyword-tabs="keywordTabs"
					:trending-keyword-section-title="
						props.pageContent.trendingKeywordSectionTitle
					"
					:is-small="isSmall"
					:is-tablet-portrait="true"
					@search="handleSearch"
					@search-click="handleSearchInputClick"
					@close-sheet="closeSheet"
					@keyword-tab-click="handleKeywordTabClick"
					@mercari-logo-click="handleMercariLogoClick"
					class="mx-[-32px] hidden mdp:block lg:mx-0"
				/>

				<header class="flex flex-row items-center gap-8px mdp:hidden">
					<!-- Back button -->
					<a
						:href="goBackURL"
						class="flex flex-row items-center gap-12px !no-underline"
					>
						<BaseIcon
							name="arrow_back"
							class="flex h-20px w-20px items-center justify-center fill-n-950"
						></BaseIcon>
						<span
							class="typography-s1-reg hidden text-n-950 no-underline mdp:block"
						>
							{{ props.pageContent.backButtonText }}
						</span>
					</a>

					<!-- Search input for desktop -->
					<div
						class="lg:py-40px mx-auto hidden w-full rounded-lg mdp:block mdp:bg-o-75 mdp:py-32px"
					>
						<!-- Desktop search input -->
						<div class="mx-auto w-full mdp:max-w-[420px] lg:max-w-[500px]">
							<SearchInput
								v-model="tempInputValue"
								:onSearch="handleSearch"
								:placeholder="props.pageContent.searchPlaceholder"
								@click="handleSearchInputClick"
							/>
						</div>
					</div>

					<!-- Mobile search input that triggers sheet -->
					<div class="block w-full mdp:hidden">
						<Sheet v-model:open="isSheetOpen">
							<SheetTrigger as-child>
								<div class="w-full">
									<SearchInput
										v-model="tempInputValue"
										:onSearch="handleSearch"
										:placeholder="props.pageContent.searchPlaceholder"
										@click="handleSearchInputClick"
									/>
								</div>
							</SheetTrigger>
							<SheetContent side="right" class="w-full">
								<div
									class="flex h-full flex-col gap-20px bg-white px-24px py-20px"
								>
									<div class="flex items-center gap-16px">
										<button
											@click="closeSheet"
											class="flex items-center bg-white"
										>
											<BaseIcon
												name="arrow_left_new"
												class="bs-icon flex h-24px w-24px items-center justify-center fill-n-950"
											></BaseIcon>
										</button>
										<div class="w-full">
											<SearchInput
												v-model="tempInputValue"
												:onSearch="handleSearch"
												:placeholder="props.pageContent.searchPlaceholder"
												@click="handleSearchInputClick"
											/>
										</div>
									</div>
									<div class="flex-1 overflow-y-auto bg-white">
										<!-- Can put content here -->
									</div>
								</div>
							</SheetContent>
						</Sheet>
					</div>
				</header>

				<main>
					<div>
						<div class="flex gap-24px">
							<div class="hidden w-[230px] lg:block">
								<MercariFilterPanel
									v-if="productListRef"
									:filter-instance="mercariFilter"
									:filter-content="props.pageContent.filterContent"
									:location-id="GaGeneralPageName.MercariIndex"
									@category-changed="handleCategoryChanged"
								/>
							</div>

							<div class="flex-1">
								<!-- Search result title and sort options in same row -->
								<div class="mb-20px">
									<div
										class="flex items-center justify-center mdp:justify-between"
									>
										<!-- Search result title - reserve space with min-height -->
										<div class="mdp:mr-16px">
											<template v-if="shouldShowSearchResultsTitle">
												<!-- Mobile -->
												<div
													class="flex flex-col flex-wrap items-center justify-center gap-8px text-n-500 mdp:hidden"
													:class="searchResultsTitleClass"
												>
													<span class="text-center">
														{{ searchResultsTitle }}
													</span>
													<button
														v-if="shouldShowTranslateButton"
														class="cursor-pointer underline"
														@click="handleTranslateButtonClick"
													>
														{{
															isTranslated
																? props.pageContent.translate?.translateOriginal
																: props.pageContent.translate?.translateTarget
														}}
													</button>
												</div>
												<!-- Desktop -->
												<div
													class="hidden flex-col items-start justify-start gap-12px text-n-500 mdp:flex mdp:flex-row mdp:items-center"
													:class="searchResultsTitleClass"
												>
													<div class="flex min-w-0 flex-1 items-center gap-8px">
														<span class="max-w-[450px] truncate">
															{{ searchResultsTitle }}
														</span>
														<button
															v-if="shouldShowTranslateButton"
															class="flex-shrink-0 cursor-pointer underline"
															@click="handleTranslateButtonClick"
														>
															{{
																isTranslated
																	? props.pageContent.translate
																			?.translateOriginal
																	: props.pageContent.translate?.translateTarget
															}}
														</button>
													</div>
												</div>
											</template>
										</div>
										<!-- tablet: sort and filter options -->
										<div
											class="hidden flex-shrink-0 items-center gap-8px mdp:flex lg:hidden"
										>
											<div class="flex items-center gap-8px">
												<button
													@click="handleSortModalOpen"
													class="flex items-center justify-center gap-8px rounded-lg border border-n-200 bg-white px-16px py-12px shadow-sm"
												>
													<BaseIcon name="exchange" class="h-[18px] w-[18px]" />
													<span class="typography-c1-reg">
														{{ pageContent.filterContent.sort.title }}
													</span>
												</button>

												<button
													@click="handleFilterButtonClick"
													class="relative flex items-center justify-center gap-8px rounded-lg border border-n-200 bg-white px-16px py-12px shadow-sm"
												>
													<BaseIcon
														name="filter_v3"
														class="h-[18px] w-[18px]"
													/>
													<span class="typography-c1-reg">
														{{ pageContent.filterContent.title }}
													</span>
													<div
														v-if="getActiveFilterCount() > 0"
														class="absolute right-8px top-8px flex h-8px w-8px items-center justify-center rounded-full bg-o-500 text-[10px] text-white"
													></div>
												</button>
											</div>
										</div>

										<!-- desktop: filter and sort options -->
										<div
											class="hidden flex-shrink-0 items-center gap-8px lg:flex"
										>
											<span class="typography-s1-reg text-gray-500">
												{{ pageContent.filterContent.sort.title }}:
											</span>
											<div class="w-[180px]">
												<select
													:value="filterState.sort"
													@change="handleSortChange"
													class="typography-c1-reg w-full cursor-pointer rounded-lg border border-n-200 bg-white p-12px text-n-950 outline-none hover:border-o-200 focus:border-o-400 focus:ring-1 focus:ring-o-200"
												>
													<option
														v-for="option in pageContent.filterContent.sort
															.options"
														:key="option.id"
														:value="option.id"
													>
														{{ option.label }}
													</option>
												</select>
											</div>
										</div>
									</div>
								</div>
								<!-- product list -->
								<MercariProductList
									ref="productListRef"
									:key="`${apiKeyword}-${apiShopId}`"
									:keyword="apiKeyword"
									:shop-id="apiShopId"
									:item-content="props.pageContent.productItem"
									:load-more-button-text="
										props.pageContent.productListLoadMoreButtonText
									"
									:edge-case="props.pageContent.edgeCase"
									:filter-state="{
										...mercariFilter.filterState,
										categoryLevels: mercariFilter.categoryLevels,
									}"
									:is-translated="isTranslated"
									@item-click="handleItemClick"
									@fetch-complete="handleFetchComplete"
								/>
							</div>
						</div>

						<div
							class="fixed bottom-[88px] right-20px"
							v-show="isShowBackToTopButton"
						>
							<BackButton @click="scrollToTop" />
						</div>

						<!-- mobile: sort and filter options -->
						<div
							class="fixed bottom-48px left-0 right-0 z-30 flex w-full bg-transparent mdp:hidden"
						>
							<div class="mx-auto w-auto">
								<div
									class="flex w-full overflow-hidden rounded-full bg-white shadow-sm"
								>
									<button
										@click="handleSortSheetOpen"
										class="flex w-full flex-1 items-center justify-center gap-8px border-r border-n-200 py-12px pl-20px pr-12px"
									>
										<BaseIcon name="exchange" class="h-16px w-16px" />
										<span class="typography-l1-emp text-n-950">
											{{ pageContent.filterContent.sort.title }}
										</span>
									</button>
									<button
										@click="handleFilterButtonClick"
										class="relative flex w-full flex-1 items-center justify-center gap-8px py-12px pl-12px pr-20px"
									>
										<BaseIcon name="filter_v3" class="h-16px w-16px" />
										<span class="typography-l1-emp text-n-950">
											{{ pageContent.filterContent.title }}
										</span>
										<div
											v-if="getActiveFilterCount() > 0"
											class="absolute right-12px top-8px flex h-8px w-8px items-center justify-center rounded-full bg-o-500 text-[10px] text-white"
										></div>
									</button>
								</div>
							</div>
						</div>
					</div>
				</main>
			</div>
		</div>
	</section>

	<MercariSortModal
		v-model:isOpen="isSortModalOpen"
		:sortOptions="pageContent.filterContent.sort.options"
		:defaultValue="filterState.sort"
		:filterContent="props.pageContent.filterContent"
		@apply="handleSortModalApply"
	/>

	<MercariSortSheet
		v-model:isOpen="isSortSheetOpen"
		:sortOptions="pageContent.filterContent.sort.options"
		:defaultValue="filterState.sort"
		:filterContent="props.pageContent.filterContent"
		@apply="handleSortModalApply"
	/>

	<MercariFilterBottomSheet
		v-model:isOpen="isFilterBottomSheetOpen"
		:filterContent="props.pageContent.filterContent"
		:filterInstance="mercariFilter"
	/>

	<MercariFilterDrawer
		v-model:isOpen="isFilterDrawerOpen"
		:filterContent="props.pageContent.filterContent"
		:filterInstance="mercariFilter"
	/>
</template>
